<template>
    <div
        v-if="isOpen"
        class="modal fade show"
        style="display: block; background: #1c1d1c33"
    >
        <div class="modal-dialog modal-dialog-centered modal-lg">
            <div class="modal-content">
                <div class="d-flex justify-content-between px-1 my-1 align-item-center">
                    <h4 class="mb-0">Edit Tax Certificate</h4>
                    <button
                        @click="toggleModal"
                        type="button"
                        class="btn-close"
                        aria-label="Close"
                    ></button>
                </div>
                <hr class="mt-0">
                <div class="px-2 pb-2">
                    <div class="mb-2">
                        <strong>Certificate No:</strong> {{ certificate.certificate_number }}
                    </div>
                    <div v-if="isTds" class="row mb-2">
                        <div class="col-md-6">
                            <strong>Payee:</strong> {{ payeeName }}
                        </div>
                    </div>
                    <div class="table-responsive">
                        <table v-if="isTds" class="table table-sm table-bordered">
                            <thead>
                                <tr>
                                    <th class="text-center">SL</th>
                                    <th>Supplier Name</th>
                                    <th>TIN</th>
                                    <th>Bill No</th>
                                    <th>Payment Date</th>
                                    <th>Section</th>
                                    <th class="text-right">Base Amount</th>
                                    <th class="text-right">TDS Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(bill, i) in bills" :key="i">
                                    <td class="text-center">{{ i + 1 }}</td>
                                    <td>{{ partyNameWithSerial(bill) }}</td>
                                    <td>{{ bill.party_tin_no }}</td>
                                    <td>{{ bill.bill_number }}</td>
                                    <td>{{ bill.payment_date }}</td>
                                    <td>{{ bill.tds_code }}</td>
                                    <td class="text-right">{{ commaFormat(bill.base_amount) }}</td>
                                    <td class="text-right">{{ commaFormat(bill.tds_amount) }}</td>
                                </tr>
                            </tbody>
                            <tfoot>
                                <tr>
                                    <th colspan="6" class="text-right">Total</th>
                                    <th class="text-right">{{ commaFormat(totalBaseAmount) }}</th>
                                    <th class="text-right">{{ commaFormat(totalTdsAmount) }}</th>
                                </tr>
                            </tfoot>
                        </table>
                        <table v-else class="table table-sm table-bordered">
                            <thead>
                                <tr>
                                    <th>Supplier Name</th>
                                    <th>BIN</th>
                                    <th>Bill Ref No</th>
                                    <th>Bill Ref Date</th>
                                    <th class="text-right">Total Amount</th>
                                    <th class="text-right">VAT Amount</th>
                                    <th class="text-right">VDS Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(bill, i) in bills" :key="i">
                                    <td>{{ bill.party_name }}</td>
                                    <td>{{ bill.party_bin_no }}</td>
                                    <td>{{ bill.bill_referance_no }}</td>
                                    <td>{{ bill.bill_referance_date }}</td>
                                    <td class="text-right">{{ commaFormat(bill.bill_amount) }}</td>
                                    <td class="text-right">{{ commaFormat(bill.vat_amount) }}</td>
                                    <td class="text-right">{{ commaFormat(bill.vds_amount) }}</td>
                                </tr>
                            </tbody>
                        </table>
                        <p v-if="!bills.length" class="text-center mt-2">No bills</p>
                    </div>
                    <div class="row mt-2">
                        <div class="mb-2" :class="isTds ? 'col-md-4' : 'col-12'">
                            <label class="form-label">Issue Date</label>
                            <input type="date" class="form-control" v-model="formData.date">
                        </div>
                        <div v-if="isTds" class="col-md-4 mb-2">
                            <label class="form-label">From Date</label>
                            <input type="date" class="form-control" v-model="formData.from_date">
                        </div>
                        <div v-if="isTds" class="col-md-4 mb-2">
                            <label class="form-label">To Date</label>
                            <input type="date" class="form-control" v-model="formData.to_date">
                        </div>
                        <div v-if="isTds" class="col-12 mb-2">
                            <label class="form-label">Issued By</label>
                            <AsyncSelect
                                placeholder="Select Issued By"
                                v-model="issuedBy"
                                :api-service="fetchContactProfiles"
                                :format-label="formatPatientLabel"
                                :additional-query="{ roles: 'employee' }"
                            />
                        </div>
                    </div>
                    <div class="text-center">
                        <hr class="mt-2">
                        <button
                            :disabled="loading"
                            @click="submit"
                            class="btn btn-primary me-1 waves-effect waves-float waves-light"
                        >
                            <div v-if="loading" class="spinner-border spinner-border-sm text-light" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                            Update
                        </button>
                        <button
                            :disabled="loading"
                            @click="remove"
                            class="btn btn-danger me-1 waves-effect waves-float waves-light"
                        >
                            <i class="fas fa-trash me-1"></i>
                            Delete
                        </button>
                        <button @click="toggleModal" class="btn btn-outline-secondary waves-effect">
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import figureFormatter from '@/services/utils/figureFormatter';
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue';
import handleContact from '@/services/modules/contact';
import { useAsyncDropdownHelper } from '@/services/utils/asyncDropdownHelper';

const props = defineProps({
    formData: {
        type: Object,
        required: true
    },
    certificate: {
        type: Object,
        default: () => ({})
    },
    bills: {
        type: Array,
        default: () => []
    },
    payee: {
        type: Object,
        default: null
    },
    issuer: {
        type: Object,
        default: null
    }
});

const emit = defineEmits(['onSubmit', 'onDelete']);

const { commaFormat } = figureFormatter();
const { fetchContactProfiles } = handleContact();
const { formatPatientLabel } = useAsyncDropdownHelper();
const isOpen = ref(false);
const loading = ref(false);
const issuedBy = ref(null);

const isTds = computed(() => props.certificate.type === 'tds_certificate');

watch(issuedBy, (newValue) => {
    if (!newValue) {
        props.formData.contact_profile_id = null;
        return;
    }
    props.formData.contact_profile_id = newValue.id;
});

const sumBy = (key) => {
    return props.bills.reduce((total, bill) => total + parseFloat(bill[key] || 0), 0);
}

const partyNameWithSerial = (bill) => {
    if (!bill.party_name) return '';
    if (!bill.party_serial_no) return bill.party_name;
    return `${bill.party_name} (${bill.party_serial_no})`;
}

const partySerialNo = computed(() => {
    const found = props.bills.find(bill => !!bill.party_serial_no);
    if (!found) return '';
    return found.party_serial_no;
})

const payeeName = computed(() => {
    if (!props.payee) return '';
    if (!props.payee.full_name) return '';
    if (!partySerialNo.value) return props.payee.full_name;
    return `${props.payee.full_name} (${partySerialNo.value})`;
})

const totalBaseAmount = computed(() => sumBy('base_amount'));
const totalTdsAmount = computed(() => sumBy('tds_amount'));

const setIssuedByFromIssuer = () => {
    if (!props.issuer) {
        issuedBy.value = null;
        return;
    }
    issuedBy.value = {
        id: props.issuer.id,
        name: props.issuer.full_name
    };
};

watch(() => props.issuer, () => {
    if (!isOpen.value) return;
    setIssuedByFromIssuer();
});

const toggleModal = () => {
    isOpen.value = !isOpen.value;
    if (!isOpen.value) {
        issuedBy.value = null;
        return;
    }
    setIssuedByFromIssuer();
};

const submit = () => {
    emit('onSubmit');
};

const remove = () => {
    emit('onDelete');
};

defineExpose({ toggleModal, loading });
</script>
